import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools } from 'zustand/middleware';
import { addData } from '../services/centralized';

export interface Project {
  id: string;
  name: string;
  description: string;
  units: {
    length: 'mm' | 'cm' | 'm' | 'in' | 'ft';
    force: 'N' | 'kN' | 'MN' | 'lbf' | 'kip';
    stress: 'Pa' | 'kPa' | 'MPa' | 'GPa' | 'psi' | 'ksi';
    temperature: 'C' | 'F' | 'K';
  };
  created: number;
  modified: number;
  version: string;
  filePath?: string;
  autoSave: boolean;
}

interface ProjectState {
  currentProject: Project | null;
  recentProjects: Project[];
  unsavedChanges: boolean;
  autoSaveInterval: number; // minutes
  lastAutoSave: number;
}

interface ProjectActions {
  initializeProject: () => void;
  createNewProject: (name: string, description?: string) => void;
  loadProject: (projectData: Project) => void;
  saveProject: () => Promise<boolean>;
  saveProjectAs: (filePath: string) => Promise<boolean>;
  closeProject: () => void;
  setProjectProperty: <K extends keyof Project>(key: K, value: Project[K]) => void;
  markUnsaved: () => void;
  markSaved: () => void;
  addToRecentProjects: (project: Project) => void;
  removeFromRecentProjects: (projectId: string) => void;
  setAutoSaveInterval: (minutes: number) => void;
  performAutoSave: () => Promise<void>;
}

const defaultProject: Omit<Project, 'id' | 'created' | 'modified'> = {
  name: 'Untitled Project',
  description: '',
  units: {
    length: 'm',
    force: 'kN',
    stress: 'MPa',
    temperature: 'C',
  },
  version: '1.0.0',
  autoSave: true,
};

const initialState: ProjectState = {
  currentProject: null,
  recentProjects: [],
  unsavedChanges: false,
  autoSaveInterval: 5, // 5 minutes
  lastAutoSave: 0,
};

export const useProjectStore = create<ProjectState & ProjectActions>()(
  devtools(
    immer((set, get) => ({
      ...initialState,
      
      initializeProject: () => {
        // Load recent projects from localStorage
        const stored = localStorage.getItem('atcivil-recent-projects');
        if (stored) {
          try {
            const recentProjects = JSON.parse(stored);
            set((state) => {
              state.recentProjects = recentProjects;
            });
          } catch (error) {
            console.error('Failed to load recent projects:', error);
          }
        }
      },
      
      createNewProject: (name, description = '') => {
        const project: Project = {
          ...defaultProject,
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          name,
          description,
          created: Date.now(),
          modified: Date.now(),
        };
        
        set((state) => {
          state.currentProject = project;
          state.unsavedChanges = false;
        });
      },
      
      loadProject: (projectData) => {
        set((state) => {
          state.currentProject = projectData;
          state.unsavedChanges = false;
        });
        
        get().addToRecentProjects(projectData);
      },
      
      saveProject: async () => {
        const { currentProject } = get();
        if (!currentProject) return false;
        
        try {
          if (window.electronAPI) {
            const result = await window.electronAPI.saveProject(currentProject);
            if (result.success && result.filePath) {
              set((state) => {
                if (state.currentProject) {
                  state.currentProject.filePath = result.filePath;
                  state.currentProject.modified = Date.now();
                  state.unsavedChanges = false;
                  state.lastAutoSave = Date.now();
                }
              });
              return true;
            }
          }
          return false;
        } catch (error) {
          console.error('Failed to save project:', error);
          return false;
        }
      },
      
      saveProjectAs: async (filePath) => {
        const { currentProject } = get();
        if (!currentProject) return false;
        
        try {
          const projectToSave = {
            ...currentProject,
            filePath,
            modified: Date.now(),
          };
          
          if (window.electronAPI) {
            const result = await window.electronAPI.saveProject(projectToSave);
            if (result.success) {
              set((state) => {
                state.currentProject = projectToSave;
                state.unsavedChanges = false;
                state.lastAutoSave = Date.now();
              });
              return true;
            }
          }
          return false;
        } catch (error) {
          console.error('Failed to save project as:', error);
          return false;
        }
      },
      
      closeProject: () => {
        set((state) => {
          state.currentProject = null;
          state.unsavedChanges = false;
        });
      },
      
      setProjectProperty: (key, value) => {
        set((state) => {
          if (state.currentProject) {
            (state.currentProject as any)[key] = value;
            state.currentProject.modified = Date.now();
            state.unsavedChanges = true;
          }
        });
      },
      
      markUnsaved: () => {
        set((state) => {
          state.unsavedChanges = true;
          if (state.currentProject) {
            state.currentProject.modified = Date.now();
          }
        });
      },
      
      markSaved: () => {
        set((state) => {
          state.unsavedChanges = false;
          state.lastAutoSave = Date.now();
        });
      },
      
      addToRecentProjects: (project) => {
        set((state) => {
          // Remove if already exists
          state.recentProjects = state.recentProjects.filter(p => p.id !== project.id);
          // Add to beginning
          state.recentProjects.unshift(project);
          // Keep only last 10
          state.recentProjects = state.recentProjects.slice(0, 10);
        });
        
        // Save to localStorage
        try {
          localStorage.setItem('atcivil-recent-projects', JSON.stringify(get().recentProjects));
        } catch (error) {
          console.error('Failed to save recent projects:', error);
        }
      },
      
      removeFromRecentProjects: (projectId) => {
        set((state) => {
          state.recentProjects = state.recentProjects.filter(p => p.id !== projectId);
        });
        
        // Save to localStorage
        try {
          localStorage.setItem('atcivil-recent-projects', JSON.stringify(get().recentProjects));
        } catch (error) {
          console.error('Failed to save recent projects:', error);
        }
      },
      
      setAutoSaveInterval: (minutes) => {
        set((state) => {
          state.autoSaveInterval = Math.max(1, minutes);
        });
      },
      
      performAutoSave: async () => {
        const { currentProject, unsavedChanges, autoSaveInterval, lastAutoSave } = get();
        
        if (!currentProject || !unsavedChanges || !currentProject.autoSave) {
          return;
        }
        
        const now = Date.now();
        const timeSinceLastSave = now - lastAutoSave;
        const autoSaveIntervalMs = autoSaveInterval * 60 * 1000;
        
        if (timeSinceLastSave >= autoSaveIntervalMs) {
          try {
            if (window.electronAPI) {
              await window.electronAPI.autoSave(currentProject);
              set((state) => {
                state.lastAutoSave = now;
              });
            }
          } catch (error) {
            console.error('Auto-save failed:', error);
          }
        }
      },
    })),
    {
      name: 'project-store',
    }
  )
);

// Subscribe to ProjectStore and publish to centralized store
useProjectStore.subscribe((state) => {
  addData("project", state.currentProject);
  addData("projectMeta", {
    unsavedChanges: state.unsavedChanges,
    autoSaveInterval: state.autoSaveInterval,
    lastAutoSave: state.lastAutoSave,
  });
  console.log(
    "✅ [ProjectStore→Centralized] Published project:",
    state.currentProject?.name || 'None'
  );
});
